import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { mediaOutletService } from '@/services/mediaOutletService';
import type { MediaOutlet } from '@/types/mediaOutlets';

interface MediaOutletDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  outlet: MediaOutlet | null;
}

interface FormData {
  outlet_name: string;
  outlet_category: string;
  display_order: number;
  is_active: boolean;
  is_popular: boolean;
}

export function MediaOutletDialog({ open, onOpenChange, outlet }: MediaOutletDialogProps) {
  const queryClient = useQueryClient();
  const isEditing = !!outlet;

  const { register, handleSubmit, reset, setValue, watch } = useForm<FormData>({
    defaultValues: {
      outlet_name: '',
      outlet_category: 'tv',
      display_order: 0,
      is_active: true,
      is_popular: false,
    },
  });

  useEffect(() => {
    if (outlet) {
      reset({
        outlet_name: outlet.outlet_name,
        outlet_category: outlet.outlet_category,
        display_order: outlet.display_order,
        is_active: outlet.is_active,
        is_popular: outlet.is_popular,
      });
    } else {
      reset({
        outlet_name: '',
        outlet_category: 'tv',
        display_order: 0,
        is_active: true,
        is_popular: false,
      });
    }
  }, [outlet, reset]);

  const saveMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const user = (await import('@/integrations/supabase/client').then(m => m.supabase.auth.getUser())).data.user;
      if (!user) throw new Error('Not authenticated');
      
      const outletData = {
        ...data,
        created_by: user.id,
        created_by_type: 'supabase' as const,
      };
      
      if (isEditing) {
        return mediaOutletService.updateOutlet(outlet.id, outletData);
      } else {
        return mediaOutletService.createOutlet(outletData);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-outlets'] });
      toast.success(isEditing ? 'Outlet updated successfully' : 'Outlet created successfully');
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to save outlet');
    },
  });

  const onSubmit = (data: FormData) => {
    saveMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit' : 'Add'} Media Outlet</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="outlet_name">Outlet Name</Label>
            <Input
              id="outlet_name"
              placeholder="e.g., ESPN, Fox Sports"
              {...register('outlet_name', { required: true })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="outlet_category">Outlet Category</Label>
            <Select
              value={watch('outlet_category')}
              onValueChange={(value) => setValue('outlet_category', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tv">TV</SelectItem>
                <SelectItem value="radio">Radio</SelectItem>
                <SelectItem value="newspaper">Newspaper</SelectItem>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="podcast">Podcast</SelectItem>
                <SelectItem value="social_media">Social Media</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="is_popular">Mark as Popular</Label>
            <Switch
              id="is_popular"
              checked={watch('is_popular')}
              onCheckedChange={(checked) => setValue('is_popular', checked)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="display_order">Display Order</Label>
            <Input
              id="display_order"
              type="number"
              {...register('display_order', { valueAsNumber: true })}
            />
            <p className="text-sm text-muted-foreground">
              Lower numbers appear first in the list
            </p>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="is_active">Active Status</Label>
            <Switch
              id="is_active"
              checked={watch('is_active')}
              onCheckedChange={(checked) => setValue('is_active', checked)}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
